import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  createOrderRequest,
  createSubOrderProductRequest,
  updateSubOrderProductRequest,
  getOrderByIdRequest,
  getOrdersRequest,
  deleteSubOrderProductRequest,
  deleteSubOrderRequest,
  updateSubOrderRequest
} from '@/app/modules/ECommerce/pages/miniPOS/services/api';
import Swal from 'sweetalert2';
import { useAppAliasIntlFormatter } from '@/app/hooks/formatFeatureName';

export const useCreateOrder = () => {
  return useMutation({
    mutationFn: createOrderRequest
  });
};

export const useOrders = params => {
  return useQuery({
    queryKey: ['minipos-orders', params],
    queryFn: () => getOrdersRequest(params),
    refetchInterval: 5000
  });
};

export const useOrder = orderId => {
  return useQuery({
    queryKey: ['minipos-order', orderId],
    queryFn: () => getOrderByIdRequest(orderId),
    enabled: !!orderId
  });
};

export const useAddProductToSubOrder = orderId => {
  const qc = useQueryClient();
  const formatMessage = useAppAliasIntlFormatter();

  return useMutation({
    mutationFn: payload => createSubOrderProductRequest(orderId, payload),

    onMutate: async newProduct => {
      await qc.cancelQueries(['minipos-order', orderId]);

      const previousOrder = qc.getQueryData(['minipos-order', orderId]);

      qc.setQueryData(['minipos-order', orderId], old => {
        if (!old?.subOrders) return old;

        const activeSubOrder = old.subOrders.find(o => o.status === 'active');
        if (!activeSubOrder) return old;

        const existingProduct = activeSubOrder.shopProducts.find(p => p.variantId === newProduct.variantId);

        if (existingProduct) {
          existingProduct.quantity += newProduct.quantity || 1;
        } else {
          activeSubOrder.shopProducts.push({
            ...newProduct,
            _id: `temp-${Date.now()}`
          });
        }

        return { ...old };
      });

      return { previousOrder };
    },

    onError: (_err, _newProduct, context) => {
      if (context?.previousOrder) {
        qc.setQueryData(['minipos-order', orderId], context.previousOrder);
      }
      Swal.fire({
        icon: 'error',
        text: _err?.response?.data?.message || formatMessage({ id: 'error' }),
        timer: 2000,
        showConfirmButton: false
      });
    },

    onSettled: () => {
      qc.invalidateQueries(['minipos-order', orderId]);
    }
  });
};

export const useUpdateSubOrderProduct = orderId => {
  const qc = useQueryClient();
  const formatMessage = useAppAliasIntlFormatter();

  return useMutation({
    mutationFn: ({ subOrderId, productId, ...payload }) =>
      updateSubOrderProductRequest(orderId, subOrderId, productId, payload),

    onMutate: async ({ subOrderId, productId, ...payload }) => {
      await qc.cancelQueries(['minipos-order', orderId]);

      const previousOrder = qc.getQueryData(['minipos-order', orderId]);

      qc.setQueryData(['minipos-order', orderId], old => {
        if (!old?.subOrders) return old;

        const subOrder = old.subOrders.find(o => o._id === subOrderId);
        if (!subOrder) return old;

        const product = subOrder.shopProducts.find(p => p._id === productId);
        if (!product) return old;

        Object.assign(product, payload);
        return { ...old };
      });

      return { previousOrder };
    },

    onError: (_err, _vars, context) => {
      if (context?.previousOrder) {
        qc.setQueryData(['minipos-order', orderId], context.previousOrder);
      }
      Swal.fire({
        icon: 'error',
        text: _err?.response?.data?.message || formatMessage({ id: 'error' }),
        timer: 2000,
        showConfirmButton: false
      });
    },

    onSettled: () => {
      qc.invalidateQueries(['minipos-order', orderId]);
    }
  });
};

export const useDeleteSubOrderProduct = orderId => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ subOrderId, productId }) => deleteSubOrderProductRequest(orderId, subOrderId, productId),

    onMutate: async ({ subOrderId, productId }) => {
      await qc.cancelQueries(['minipos-order', orderId]);

      const previousOrder = qc.getQueryData(['minipos-order', orderId]);

      qc.setQueryData(['minipos-order', orderId], old => {
        if (!old?.subOrders) return old;

        const subOrder = old.subOrders.find(o => o._id === subOrderId);
        if (!subOrder) return old;

        subOrder.shopProducts = subOrder.shopProducts.filter(p => p._id !== productId);

        return { ...old };
      });

      return { previousOrder };
    },

    onError: (_err, _vars, context) => {
      if (context?.previousOrder) {
        qc.setQueryData(['minipos-order', orderId], context.previousOrder);
      }
    },

    onSettled: () => {
      qc.invalidateQueries(['minipos-order', orderId]);
    }
  });
};

export const useDeleteSubOrder = orderId => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: subOrderId => deleteSubOrderRequest(orderId, subOrderId),

    onMutate: async subOrderId => {
      await qc.cancelQueries(['minipos-order', orderId]);

      const previousOrder = qc.getQueryData(['minipos-order', orderId]);

      qc.setQueryData(['minipos-order', orderId], old => {
        if (!old?.subOrders) return old;

        return {
          ...old,
          subOrders: old.subOrders.filter(s => s._id !== subOrderId)
        };
      });

      return { previousOrder };
    },

    onError: (_err, _vars, context) => {
      if (context?.previousOrder) {
        qc.setQueryData(['minipos-order', orderId], context.previousOrder);
      }
    },

    onSettled: () => {
      qc.invalidateQueries(['minipos-order', orderId]);
    }
  });
};

export const useUpdateSubOrder = orderId => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subOrderId, ...payload }) => updateSubOrderRequest(orderId, subOrderId, payload),
    onMutate: async ({ subOrderId, ...payload }) => {
      await qc.cancelQueries(['minipos-order', orderId]);
      const previousOrder = qc.getQueryData(['minipos-order', orderId]);
      qc.setQueryData(['minipos-order', orderId], old => {
        if (!old?.subOrders) return old;
        const sub = old.subOrders.find(s => s._id === subOrderId);
        if (!sub) return old;
        Object.assign(sub, payload);
        return { ...old };
      });
      return { previousOrder };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousOrder) {
        qc.setQueryData(['minipos-order', orderId], context.previousOrder);
      }
    },
    onSettled: () => {
      qc.invalidateQueries(['minipos-order', orderId]);
    }
  });
};
